import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { LLink as Link } from '../components/LocalizedLink';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Tag, ThumbsUp, ThumbsDown, Award, Globe } from 'lucide-react';
import { insurersApi, uploadsApi } from '../lib/api';
import { articleToSafeHtml } from '../lib/pasteSanitizer';
import { useI18n } from '../context/I18nContext';

export default function InsurerArticle() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    insurersApi
      .getArticle(slug, lang)
      .then((a) => setArticle(a))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  if (loading) {
    return (
      <div className="container-x py-24 text-center text-slate-500" data-testid="insurer-article-loading">
        Loading...
      </div>
    );
  }
  if (notFound) return <Navigate to="/blog" replace />;
  if (!article) return null;

  const cover = article.cover_image ? uploadsApi.publicUrl(article.cover_image) : null;
  const dateISO = article.updated_at || article.created_at;
  const date = dateISO
    ? new Date(dateISO).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div>
      <Helmet>
        <title>{article.seo_title || article.title}</title>
        {article.seo_description && <meta name="description" content={article.seo_description} />}
        {article.seo_keywords && <meta name="keywords" content={article.seo_keywords} />}
        <meta property="og:title" content={article.seo_title || article.title} />
        {article.seo_description && (
          <meta property="og:description" content={article.seo_description} />
        )}
        <meta property="og:type" content="article" />
        {cover && <meta property="og:image" content={cover} />}
      </Helmet>

      <section className="hero-bg">
        <div className="container-x pt-14 pb-10 max-w-3xl">
          <Link
            to="/blog"
            className="text-sm text-slate-500 hover:text-[#0d3a8a] inline-flex items-center gap-1 mb-6"
            data-testid="insurer-article-back"
          >
            <ArrowLeft size={14} /> Back to blog
          </Link>
          <span className="eyebrow">Insurers</span>
          <h1
            className="mt-4 text-4xl md:text-5xl font-bold leading-tight text-[#0b1f3a] tracking-tight"
            data-testid="insurer-article-title"
          >
            {article.title}
          </h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-500 flex-wrap">
            {date && (
              <span className="inline-flex items-center gap-1">
                <Calendar size={14} /> {date}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Tag size={14} /> Insurers
            </span>
            {article.display_name && (
              <span className="inline-flex items-center gap-2 pl-3 border-l border-slate-200">
                {article.logo_url && (
                  <img
                    src={article.logo_url}
                    alt=""
                    className="w-6 h-6 object-contain rounded border border-slate-200 bg-white"
                  />
                )}
                <span className="font-semibold text-[#0b1f3a]">{article.display_name}</span>
              </span>
            )}
          </div>
        </div>
      </section>

      {cover && (
        <div className="container-x max-w-4xl -mt-6">
          <img
            src={cover}
            alt={article.title}
            className="w-full h-[380px] object-cover rounded-2xl shadow-lg"
            data-testid="insurer-article-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      <section className="py-14">
        <div className="container-x max-w-3xl">
          {article.excerpt && (
            <p
              className="text-lg text-slate-600 leading-relaxed border-l-4 border-[#0d3a8a] pl-5 mb-8"
              data-testid="insurer-article-excerpt"
            >
              {article.excerpt}
            </p>
          )}

          {((article.pros && article.pros.length > 0) || (article.cons && article.cons.length > 0)) && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10"
              data-testid="insurer-article-proscons"
            >
              {article.pros && article.pros.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                      <ThumbsUp size={16} />
                    </span>
                    <h3 className="font-semibold text-emerald-900">Pros</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-emerald-900" data-testid="insurer-article-pros">
                    {article.pros.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5 shrink-0">&#10003;</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {article.cons && article.cons.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 inline-flex items-center justify-center">
                      <ThumbsDown size={16} />
                    </span>
                    <h3 className="font-semibold text-rose-900">Cons</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-rose-900" data-testid="insurer-article-cons">
                    {article.cons.map((c, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-rose-600 mt-0.5 shrink-0">&minus;</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {(article.english_support || article.am_best_rating) && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="insurer-article-badges">
              {article.english_support && article.english_support !== 'unknown' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                  <Globe size={12} /> English support: <span className="capitalize">{article.english_support}</span>
                </span>
              )}
              {article.am_best_rating && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                  <Award size={12} /> AM Best {article.am_best_rating}
                </span>
              )}
              {article.founded_year && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                  Since {article.founded_year}
                </span>
              )}
            </div>
          )}

          <article
            className="prose-esi"
            data-testid="insurer-article-body"
            dangerouslySetInnerHTML={{ __html: articleToSafeHtml(article.content || '') }}
          />
        </div>
      </section>

      <section className="py-14 bg-[#f7fafd]">
        <div className="container-x max-w-3xl text-center">
          <h3 className="text-2xl font-bold text-[#0b1f3a]">Compare {article.display_name} against every other insurer</h3>
          <p className="mt-2 text-slate-600">Get a live quote in under 10 minutes.</p>
          <Link to="/home-insurance" className="btn-primary mt-5 inline-flex">
            Get your quote
          </Link>
        </div>
      </section>
    </div>
  );
}
